import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../api';
import './styles.css';

const DRIVER_TOKEN_KEY = 'driver_portal_token';

function mediaUrl(path) {
  if (!path) return '';
  if (String(path).startsWith('http')) return path;
  return `${API.defaults.baseURL.replace(/\/api\/?$/, '')}${path}`;
}

function requiredAction(status, t) {
  if (status === 'assigned') return t('Pick up the order from office');
  if (status === 'picked_up') return t('Deliver the order to customer');
  if (status === 'in_transit') return t('Confirm delivery and upload proof');
  if (status === 'offered') return t('Accept or reject the offer');
  return t('No pending action');
}

function minutesSince(value) {
  if (!value) return 0;
  const then = new Date(value).getTime();
  if (!then) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 60000));
}

export default function DriverAccessHome() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [notesByOrder, setNotesByOrder] = useState({});
  const [offerNotes, setOfferNotes] = useState({});
  const [uploadingOrderId, setUploadingOrderId] = useState(null);
  const [actionError, setActionError] = useState('');

  const token = localStorage.getItem(DRIVER_TOKEN_KEY);

  useEffect(() => {
    const storedDriverLang = localStorage.getItem('driver_portal_lang');
    if (storedDriverLang && ['ar', 'en', 'fr'].includes(storedDriverLang) && i18n.language !== storedDriverLang) {
      i18n.changeLanguage(storedDriverLang);
    }
  }, [i18n]);

  const load = useCallback(async () => {
    if (!token) {
      navigate('/driver/login');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await API.get('drivers/portal-data/', {
        skipAuthRedirect: true,
        skipAuthToken: true,
        headers: { 'X-Driver-Token': token },
      });
      setPayload(res.data || {});
    } catch (err) {
      localStorage.removeItem(DRIVER_TOKEN_KEY);
      setError(err?.response?.data?.error || t('Failed to load driver portal'));
    } finally {
      setLoading(false);
    }
  }, [navigate, t, token]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    localStorage.removeItem(DRIVER_TOKEN_KEY);
    localStorage.removeItem('driver_portal_driver');
    navigate('/driver/login');
  };

  const updateStatus = async (orderId, status) => {
    await API.post('drivers/portal-order-status/', {
      token,
      order_id: orderId,
      status,
      notes: notesByOrder[orderId] || '',
    }, { skipAuthRedirect: true, skipAuthToken: true }).catch(() => {});
    load();
  };

  const respondOffer = async (assignmentId, response) => {
    setActionError('');
    await API.post('drivers/portal-offer-response/', {
      token,
      assignment_id: assignmentId,
      response,
      reason: offerNotes[assignmentId] || '',
    }, { skipAuthRedirect: true, skipAuthToken: true }).catch((err) => {
      setActionError(err?.response?.data?.error || t('Update failed'));
    });
    load();
  };

  const setDriverStatus = async (driverStatus) => {
    await API.post('drivers/portal-set-status/', {
      token,
      driver_status: driverStatus,
    }, { skipAuthRedirect: true, skipAuthToken: true }).catch(() => {});
    load();
  };

  const uploadPhoto = async (orderId, file, photoType) => {
    if (!file) return;
    const body = new FormData();
    body.append('token', token);
    body.append('order_id', orderId);
    body.append('photo_type', photoType);
    body.append('photo', file);
    setUploadingOrderId(orderId);
    try {
      await API.post('drivers/portal-upload-photo/', body, {
        skipAuthRedirect: true,
        skipAuthToken: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } finally {
      setUploadingOrderId(null);
    }
  };

  const statusLabel = (status) => {
    const translated = t(`status.${status}`);
    return translated === `status.${status}` ? status : translated;
  };

  const driver = payload?.driver || {};
  const office = payload?.office || {};
  const activeOrders = useMemo(() => payload?.active_orders || [], [payload]);
  const completedOrders = useMemo(() => payload?.completed_orders || [], [payload]);
  const pendingOffers = useMemo(() => payload?.pending_offers || [], [payload]);
  const offerHistory = useMemo(() => payload?.offer_history || [], [payload]);
  const hasBlockingActiveOrder = useMemo(
    () => activeOrders.some((o) => ['assigned', 'picked_up', 'in_transit'].includes(o.status)),
    [activeOrders]
  );

  if (loading) {
    return <div className="login-page"><div className="card login-card">{t('Loading...')}</div></div>;
  }

  if (error) {
    return (
      <div className="login-page">
        <div className="card login-card">
          <p className="login-error">{error}</p>
          <button className="btn btn-primary" type="button" onClick={() => navigate('/driver/login')}>{t('Back')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="driver-portal-page">
      <div className="driver-portal-header">
        <div>
          <h1>{driver.name || t('Driver Portal')}</h1>
          <div className="brand-sub">{office.name || t('Delivery Management')}</div>
          <div className="mini-meta">{t('Rating')}: {Number(driver.overall_rating || 0).toFixed(2)}/5</div>
        </div>
        <div className="row-actions">
          <button className={`btn ${i18n.language === 'ar' ? 'btn-primary' : 'btn-secondary'}`} type="button" onClick={() => { i18n.changeLanguage('ar'); localStorage.setItem('driver_portal_lang', 'ar'); }}>عربي</button>
          <button className={`btn ${i18n.language === 'fr' ? 'btn-primary' : 'btn-secondary'}`} type="button" onClick={() => { i18n.changeLanguage('fr'); localStorage.setItem('driver_portal_lang', 'fr'); }}>FR</button>
          <button className={`btn ${i18n.language === 'en' ? 'btn-primary' : 'btn-secondary'}`} type="button" onClick={() => { i18n.changeLanguage('en'); localStorage.setItem('driver_portal_lang', 'en'); }}>EN</button>
          <span className={`badge ${driver.driver_status}`}>{statusLabel(driver.driver_status || 'online')}</span>
          <button className="btn btn-success" type="button" onClick={() => setDriverStatus('online')}>{t('Online')}</button>
          <button className="btn btn-warning" type="button" onClick={() => setDriverStatus('busy')}>{t('Busy')}</button>
          <button className="btn" type="button" onClick={() => setDriverStatus('offline')}>{t('Offline')}</button>
          <button className="btn btn-secondary" type="button" onClick={logout}>{t('Logout')}</button>
        </div>
      </div>

      {payload?.hide_customer_phones_from_driver && (
        <div className="card" style={{ marginBottom: 16 }}>
          <strong>{t('Phone numbers are hidden by office')}</strong>
        </div>
      )}

      {actionError && (
        <div className="card" style={{ marginBottom: 16, borderColor: '#ef4444' }}>
          <strong>{actionError}</strong>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('Pending Offers')}</h3>
        {hasBlockingActiveOrder && <p className="mini-meta">{t('Complete current active order before accepting a new offer')}</p>}
        {pendingOffers.length === 0 && <p>{t('No pending offers for this driver.')}</p>}
        <div className="order-grid">
          {pendingOffers.map((offer) => (
            <div className="order-card" key={offer.assignment_id}>
              <div className="order-number">#{offer.order_number}</div>
              <p><strong>{t('Sender')}:</strong> {offer.customer_name || '-'}</p>
              <p><strong>{t('Recipient')}:</strong> {offer.recipient_name || '-'}</p>
              <p><strong>{t('Pickup Address')}:</strong> {offer.pickup_address}</p>
              <p><strong>{t('Delivery Address')}:</strong> {offer.delivery_address}</p>
              <p><strong>{t('Priority')}:</strong> {t(offer.priority)}</p>
              <p><strong>{t('Offer age')}:</strong> {minutesSince(offer.offered_at)} {t('min')}</p>
              <textarea
                className="input"
                placeholder={t('Reason if rejecting')}
                value={offerNotes[offer.assignment_id] || ''}
                onChange={(event) => setOfferNotes((prev) => ({ ...prev, [offer.assignment_id]: event.target.value }))}
              />
              <div className="row-actions">
                <button className="btn btn-success" type="button" onClick={() => respondOffer(offer.assignment_id, 'accept')} disabled={hasBlockingActiveOrder}>{t('Accept')}</button>
                <button className="btn btn-danger" type="button" onClick={() => respondOffer(offer.assignment_id, 'reject')}>{t('Reject')}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('Offer History')}</h3>
        {offerHistory.length === 0 && <p>{t('No offer history yet')}</p>}
        <div className="order-grid">
          {offerHistory.map((item) => (
            <div className="order-card" key={item.assignment_id}>
              <div className="row-between">
                <div className="order-number">#{item.order_number}</div>
                <span className={`badge ${item.status === 'accepted' ? 'delivered' : 'cancelled'}`}>{t(item.status)}</span>
              </div>
              <p><strong>{t('Offered')}:</strong> {item.offered_at ? new Date(item.offered_at).toLocaleString() : '-'}</p>
              <p><strong>{t('Responded')}:</strong> {item.responded_at ? new Date(item.responded_at).toLocaleString() : '-'}</p>
              {item.rejection_reason ? <p><strong>{t('Reason')}:</strong> {item.rejection_reason}</p> : null}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('Current Tasks')}</h3>
        {activeOrders.length === 0 && <p>{t('No active orders')}</p>}
        <div className="order-grid">
          {activeOrders.map((order) => (
            <div className="order-card" key={order.id}>
              <div className="row-between">
                <div className="order-number">#{order.order_number}</div>
                <span className={`badge ${order.status}`}>{statusLabel(order.status)}</span>
              </div>
              <p><strong>{t('Required now')}:</strong> {requiredAction(order.status, t)}</p>
              <p><strong>{t('Sender')}:</strong> {order.customer_name || '-'}</p>
              <p><strong>{t('Recipient')}:</strong> {order.recipient_name || order.recipient_customer_name || '-'}</p>
              <p><strong>{t('Source customer address')}:</strong> {order.customer_address || '-'}</p>
              <p><strong>{t('Destination customer address')}:</strong> {order.recipient_customer_address || order.delivery_address || '-'}</p>
              <p><strong>{t('Pickup Address')}:</strong> {order.pickup_address}</p>
              <p><strong>{t('Delivery Address')}:</strong> {order.delivery_address}</p>
              <p><strong>{t('Barcode')}:</strong> {order.barcode || '-'}</p>
              <p><strong>{t('COD amount')}:</strong> {order.cod_amount || '-'}</p>
              <p><strong>{t('Delivery fee')}:</strong> {order.delivery_fee || '-'}</p>
              <p><strong>{t('Office')}:</strong> {order.office_name || office.name || '-'}</p>
              <p><strong>{t('Sender phone')}:</strong> {order.customer_phone || t('Hidden by office')}</p>
              <p><strong>{t('Recipient phone')}:</strong> {order.recipient_phone || order.recipient_customer_phone || t('Hidden by office')}</p>

              <div className="proof-grid">
                <div>
                  <div className="field-label">{t('Office photo')}</div>
                  {order.office_photo ? <img className="proof-image" src={mediaUrl(order.office_photo)} alt="office proof" /> : <div className="mini-meta">{t('No office photo')}</div>}
                </div>
                <div>
                  <div className="field-label">{t('Delivery photo')}</div>
                  {order.delivery_photo ? <img className="proof-image" src={mediaUrl(order.delivery_photo)} alt="delivery proof" /> : <div className="mini-meta">{t('No delivery photo')}</div>}
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <label className="field-label">{t('Upload office proof')}</label>
                <input className="input" type="file" accept="image/*" onChange={(event) => uploadPhoto(order.id, event.target.files?.[0], 'office')} />
              </div>

              <div style={{ marginTop: 12 }}>
                <label className="field-label">{t('Upload delivery proof')}</label>
                <input className="input" type="file" accept="image/*" onChange={(event) => uploadPhoto(order.id, event.target.files?.[0], 'delivery')} />
                {uploadingOrderId === order.id && <div className="mini-meta">{t('Uploading...')}</div>}
              </div>

              <textarea
                className="input"
                style={{ marginTop: 12 }}
                placeholder={t('Notes')}
                value={notesByOrder[order.id] || ''}
                onChange={(event) => setNotesByOrder((prev) => ({ ...prev, [order.id]: event.target.value }))}
              />

              <div className="row-actions" style={{ marginTop: 12 }}>
                {order.status === 'assigned' && <button className="btn btn-primary" type="button" onClick={() => updateStatus(order.id, 'picked_up')}>{t('Mark as Picked Up')}</button>}
                {order.status === 'picked_up' && <button className="btn btn-warning" type="button" onClick={() => updateStatus(order.id, 'in_transit')}>{t('Mark as In Transit')}</button>}
                {['picked_up', 'in_transit'].includes(order.status) && <button className="btn btn-success" type="button" onClick={() => updateStatus(order.id, 'delivered')}>{t('Mark as Delivered')}</button>}
                {['assigned', 'picked_up', 'in_transit'].includes(order.status) && <button className="btn btn-danger" type="button" onClick={() => updateStatus(order.id, 'cancelled')}>{t('Cancel Order')}</button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{t('Completed Tasks')}</h3>
        {completedOrders.length === 0 && <p>{t('No completed orders yet')}</p>}
        <div className="order-grid">
          {completedOrders.map((order) => (
            <div className="order-card" key={order.id}>
              <div className="row-between">
                <div className="order-number">#{order.order_number}</div>
                <span className={`badge ${order.status}`}>{statusLabel(order.status)}</span>
              </div>
              <p><strong>{t('Sender')}:</strong> {order.customer_name || '-'}</p>
              <p><strong>{t('Recipient')}:</strong> {order.recipient_name || order.recipient_customer_name || '-'}</p>
              <p><strong>{t('Delivery Address')}:</strong> {order.delivery_address}</p>
              <p><strong>{t('Actual Delivery')}:</strong> {order.actual_delivery ? new Date(order.actual_delivery).toLocaleString() : '-'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}